import Time from './Time';
import { Sizes } from './Sizes';
import { Scene } from './Scene';
import { Camera } from './Camera';
import { Renderer } from './Renderer';
import { SectionLogo } from '../Sections/SectionLogo';
import { SectionManager } from '../Sections/SectionManager';
import EventEmitter from '../Utils/EventEmitter';
import { SmoothScroll } from './SmoothScroll';

/**
 * World class
 * Main class that ties everything together
 */
export class World extends EventEmitter {
  // Static instance property
  static instance = null;

  /**
   * Get the singleton instance of the class
   * @returns {World} The singleton instance
   */
  static getInstance() {
    if (!World.instance) {
      World.instance = new World();
    }
    return World.instance;
  }

  /**
   * Constructor
   */
  constructor() {
    // Ensure this is a singleton
    if (World.instance) {
      return World.instance;
    }

    super();

    // Initialize all components in the correct order
    this.initializeComponents();

    // Trigger ready event
    this.trigger('ready');

    // Set instance
    World.instance = this;
  }

  /**
   * Initialize all components
   */
  initializeComponents() {   
    // Core components in dependency order
    this.time = Time.getInstance();    
    this.sizes = Sizes.getInstance();
    this.scene = Scene.getInstance();
    this.camera = Camera.getInstance();
    this.renderer = Renderer.getInstance();
    this.scroll = SmoothScroll.getInstance();
    this.sectionManager = SectionManager.getInstance();
    
    if(!this.sizes.isComingSoon) {
      this.sectionLogo = SectionLogo.getInstance();
    }
  }

  /**
   * Get component by name
   * @param {String} name - Component name
   * @returns {Object} - Component instance
   */
  getComponent(name) {
    return this[name] || null;
  }

  /**
   * Update method - called on each frame
   * @param {Number} delta - Time since last frame in milliseconds
   * @param {Number} elapsed - Total time elapsed in milliseconds
   */
  update(delta, elapsed) {
    // Trigger update event
    this.trigger('update', [{ delta, elapsed }]);
  }

  logoUpdate() {
    if (this.sectionLogo) {
      this.sectionLogo.updatePosition();
    }
  }

  /**
   * Dispose all components and resources
   */
  dispose() {
    // Trigger before dispose event
    this.trigger('beforeDispose');

    // Dispose SectionLogo if it exists
    if (this.sectionLogo) {
      this.sectionLogo.dispose();
    }

    // Dispose renderer
    if (this.renderer && this.renderer.instance) {
      this.renderer.instance.dispose();
    }

    // Remove all objects from scene
    if (this.scene && this.scene.instance) {
      this.scene.instance.traverse((object) => {
        if (object.geometry) {
          object.geometry.dispose();
        }

        if (object.material) {
          if (Array.isArray(object.material)) {
            object.material.forEach(material => material.dispose());
          } else {
            object.material.dispose();
          }
        }
      });
    }

    // Trigger disposed event
    this.trigger('disposed');
  }
}
